import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeft, User, Wallet, FileText, Activity, Shield, Ban, CheckCircle, XCircle, Clock } from 'lucide-react';
import AdminNavbar from '../components/AdminNavbar';

const AdminUserDetailsPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => {
        fetchUserDetails();
    }, [userId]);

    const fetchUserDetails = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/admin/users/${userId}`);
            setData(response.data);
        } catch (error) {
            console.error('Fetch user details error:', error);
            toast.error('Failed to load user details');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (!window.confirm(`Are you sure you want to change status to ${newStatus}?`)) return;

        try {
            // Using the endpoint structure seen in routes/admin.js or similar
            // Assuming PUT /users/:userId/status
            await api.put(`/admin/users/${userId}/status`, { status: newStatus });
            toast.success(`User status updated to ${newStatus}`);
            fetchUserDetails();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleKYCReview = async (kycId, status) => {
        if (!window.confirm(`Are you sure you want to ${status} this KYC?`)) return;
        try {
            await api.put(`/admin/kyc/${kycId}/review`, { status });
            toast.success(`KYC ${status}`);
            fetchUserDetails();
        } catch (error) {
            toast.error('Failed to review KYC');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center">
                <p className="text-xl text-gray-400 mb-4">User not found</p>
                <button onClick={() => navigate('/admin/users')} className="text-blue-400 hover:text-blue-300">Go Back</button>
            </div>
        );
    }

    const { user, balances, recentOrders } = data;

    return (
        <div className="min-h-screen bg-slate-900 text-white pb-12">
            <AdminNavbar />

            <div className="container mx-auto px-6 py-8">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <button onClick={() => navigate('/admin/users')} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                        <ArrowLeft size={24} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            {user.firstName} {user.lastName}
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${user.status === 'active' ? 'bg-green-500/10 text-green-400' :
                                    user.status === 'banned' ? 'bg-red-500/10 text-red-400' :
                                        'bg-yellow-500/10 text-yellow-400'
                                }`}>
                                {user.status.toUpperCase()}
                            </span>
                        </h1>
                        <p className="text-gray-400 text-sm">{user.email}</p>
                    </div>
                    <div className="ml-auto flex gap-2">
                        {user.status !== 'banned' ? (
                            <button
                                onClick={() => handleStatusChange('banned')}
                                className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors border border-red-500/30"
                            >
                                <Ban size={18} /> Ban User
                            </button>
                        ) : (
                            <button
                                onClick={() => handleStatusChange('active')}
                                className="flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded-lg transition-colors border border-green-500/30"
                            >
                                <CheckCircle size={18} /> Unban User
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Column 1: Profile & KYC */}
                    <div className="space-y-6">
                        {/* Profile Card */}
                        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                                <User size={20} className="text-blue-400" />
                                Personal Info
                            </h2>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-400">User ID</span>
                                    <span className="font-mono">{user.id.split('-')[0]}...</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Joined</span>
                                    <span>{new Date(user.createdAt).toLocaleDateString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Email Verified</span>
                                    <span className={user.emailVerified ? 'text-green-400' : 'text-yellow-400'}>
                                        {user.emailVerified ? 'Yes' : 'No'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* KYC Card */}
                        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                                <Shield size={20} className="text-purple-400" />
                                KYC Status
                            </h2>
                            {user.kyc ? (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-400">Status</span>
                                        <span className={`px-2 py-1 rounded text-xs font-bold ${user.kyc.status === 'verified' ? 'bg-green-500/10 text-green-400' :
                                                user.kyc.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                                                    'bg-red-500/10 text-red-400'
                                            }`}>
                                            {user.kyc.status.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="p-3 bg-slate-700/50 rounded-lg text-sm space-y-2">
                                        <p><span className="text-gray-400">Full Name:</span> {user.kyc.fullName}</p>
                                        <p><span className="text-gray-400">DOB:</span> {user.kyc.dateOfBirth}</p>
                                        <p><span className="text-gray-400">Country:</span> {user.kyc.country}</p>
                                    </div>
                                    {user.kyc.status === 'pending' && (
                                        <div className="flex gap-2 pt-2">
                                            <button onClick={() => handleKYCReview(user.kyc.id, 'verified')} className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium">Approve</button>
                                            <button onClick={() => handleKYCReview(user.kyc.id, 'rejected')} className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-sm font-medium">Reject</button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-gray-400 text-sm text-center py-4">No KYC Submitted</p>
                            )}
                        </div>
                    </div>

                    {/* Column 2: Balances */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Wallet Balances */}
                        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                                <Wallet size={20} className="text-green-400" />
                                Wallet Balances
                            </h2>
                            {balances && balances.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {balances.map(b => (
                                        <div key={b.id} className="p-4 bg-slate-700/30 rounded-lg border border-slate-700 flex justify-between items-center">
                                            <div className="flex items-center gap-3">
                                                {/* Placeholder for token icon if not present in API */}
                                                <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center font-bold text-xs">{b.token.symbol[0]}</div>
                                                <div>
                                                    <p className="font-bold">{b.token.symbol}</p>
                                                    <p className="text-xs text-gray-400">{b.token.name}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-mono text-lg">{parseFloat(b.balance).toFixed(4)}</p>
                                                <p className="text-xs text-gray-400">Locked: {parseFloat(b.lockedBalance).toFixed(4)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-400 text-center">No balances found</p>
                            )}
                        </div>

                        {/* Recent Orders */}
                        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                                <Activity size={20} className="text-orange-400" />
                                Recent Orders
                            </h2>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead className="text-gray-400 font-medium border-b border-slate-700">
                                        <tr>
                                            <th className="pb-3">Pair</th>
                                            <th className="pb-3">Type</th>
                                            <th className="pb-3">Price</th>
                                            <th className="pb-3">Amount</th>
                                            <th className="pb-3">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-700">
                                        {recentOrders && recentOrders.length > 0 ? (
                                            recentOrders.map(order => (
                                                <tr key={order.id} className="hover:bg-slate-700/20">
                                                    <td className="py-3">{order.tradingPair?.symbol || order.symbol}</td>
                                                    <td className={`py-3 ${order.side === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                                                        {order.side.toUpperCase()}
                                                    </td>
                                                    <td className="py-3">{parseFloat(order.price).toFixed(2)}</td>
                                                    <td className="py-3">{parseFloat(order.amount).toFixed(4)}</td>
                                                    <td className="py-3">
                                                        <span className={`px-2 py-0.5 rounded text-xs ${order.status === 'filled' ? 'bg-blue-500/10 text-blue-400' :
                                                                order.status === 'cancelled' ? 'bg-gray-500/10 text-gray-400' :
                                                                    'bg-yellow-500/10 text-yellow-400'
                                                            }`}>
                                                            {order.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="py-4 text-center text-gray-400">No recent orders</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminUserDetailsPage;
